import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { TenantContext, useApiVersion } from "../../tenant";
import { MagicApi, Reservation } from "../../api/magic/magic.api";
import { generateSmallUUID } from "../../utils";

const getWsUrl = (tenantId: string) => {
  const protocol = window.location.protocol === "http:" ? "ws:" : "wss:";
  const host = window.location.host;
  return `${protocol}//${host}/api/salto-event-stream/magic-ws/${tenantId}`;
};

enum EventType {
  ERROR = "ERROR",
  SALTO_TAG = "SALTO_TAG",
  KEEP_ALIVE = "KEEP_ALIVE",
  SUBSCRIPTION = "SUBSCRIPTION"
}

type SubscriptionEventData =
  | { type: EventType.KEEP_ALIVE }
  | {
      type: EventType.ERROR;
      status: number;
      sessionId: string;
      messageId: string;
    }
  | {
      type: EventType.SUBSCRIPTION;
      sessionId: string;
      status: number;
      messageId: string;
      magicId: string;
    }
  | {
      type: EventType.SALTO_TAG;
      sessionId: string;
      status: number;
      messageId: string;
      magicId: string;
      tagId: string;
      tagNumber: string;
      name: string;
      isPublic: boolean;
    };

type OpenBanner = {
  title: string;
  type: "error" | "success";
};

/**
 *
 * @param reservation Reservation object
 * @param tagReaderId Identified reader by id
 * @param closeBanner ? Maybe in the future make them more generic
 * @param openBanner ? Maybe in the future make them more generic
 * @param timeoutError Timeout error label
 * @param isPageVisible Controls that assigning only start if the page is in focus
 * @param onAssignedKey Event on successfully assigned key
 * @param subscribeTimeout It delays subscribe event in ms. Default value is 3000
 */
interface UseKeyAssignProps {
  reservation: Reservation;
  tagReaderId: string;
  closeBanner: () => void;
  openBanner: (payload: OpenBanner) => void;
  timeoutError: string;
  isPageVisible: boolean;
  onAssignedKey?: () => void;
  subscribeTimeout?: number;
}

export const useKeyAssign = ({
  reservation,
  tagReaderId,
  closeBanner,
  openBanner,
  timeoutError,
  isPageVisible,
  onAssignedKey,
  subscribeTimeout
}: UseKeyAssignProps) => {
  const ws = React.useRef<WebSocket | null>(null);
  const { tenantId, apiKey } = useContext(TenantContext);
  const initializedMagicObjects = useRef<{ [magicId: string]: boolean }>({});
  const [isKeyAssigned, setKeyAssigned] = useState(false);
  const [tagNumber, setTagNumber] = useState("");
  const [isConnecting, setConnecting] = useState(false);
  const [isWaiting, setWaiting] = useState(false);
  const [isError, setError] = useState(false);
  const [isAborted, setIsAborted] = useState(false);

  const magicId = reservation?.magicId;
  const magicToken = reservation?.magicToken;
  const timeout = subscribeTimeout !== undefined ? subscribeTimeout : 3000;

  const { isRESTVersion } = useApiVersion();

  const setIsKeyAssignConnecting = useCallback(
    (value: boolean) => {
      setConnecting(value);
      if (value) {
        setIsAborted(false);
        setError(false);
        setWaiting(false);
      }
    },
    [setConnecting, setError, setWaiting]
  );

  const setIsKeyAssignWaiting = useCallback(
    (value: boolean) => {
      setWaiting(value);
      if (value) {
        setError(false);
        setConnecting(false);
      }
    },
    [setWaiting, setError]
  );

  const resetKeyAssignState = useCallback(() => {
    ws.current?.close(1000);
    ws.current = null;
    setKeyAssigned(false);
    setWaiting(false);
    setError(false);
  }, []);

  const abortKeyAssigment = useCallback(() => {
    setIsAborted(true);
    resetKeyAssignState();
  }, [resetKeyAssignState]);

  const setErrorAssigning = useCallback(() => {
    setIsAborted(false);
    setError(true);
    setKeyAssigned(false);
    setConnecting(false);
    setWaiting(false);
  }, [setError, setKeyAssigned, setConnecting, setWaiting]);

  const onAssignKeySuccess = useCallback(() => {
    setKeyAssigned(true);
    setConnecting(false);
    setIsAborted(false);
    setWaiting(false);
    setError(false);
  }, [setKeyAssigned, setConnecting, setIsAborted, setWaiting, setError]);

  const onAssignKeyError = useCallback(() => {
    setKeyAssigned(true);
    setIsAborted(false);
    setError(true);
  }, [setIsAborted, setError]);

  const assignCardCallback = useCallback(
    (reservationToAssign: Reservation, tagNumberToAssign: string) => {
      // if it not REST version, API call is done directly on the component which is using this hook when tagNumber is assigned
      if (isRESTVersion) {
        return MagicApi.assignTagToReservation(reservationToAssign, tagNumberToAssign)
          .then(() => {
            onAssignKeySuccess();
          })
          .catch(() => {
            onAssignKeyError();
            return Promise.reject();
          });
      } else {
        return Promise.resolve();
      }
    },
    [isRESTVersion, onAssignKeyError, onAssignKeySuccess]
  );

  const initializeKey = useCallback(() => {
    let isInitializedForId = magicId ? initializedMagicObjects.current[magicId] : false;

    if (!(magicId && isInitializedForId)) {
      ws.current?.close(1000);
      ws.current = null;
    }

    if (
      magicId &&
      (!isInitializedForId ||
        ws.current?.readyState === WebSocket.CLOSED ||
        ws.current?.readyState === WebSocket.CLOSING) &&
      isPageVisible
    ) {
      initializedMagicObjects.current = { [magicId]: true };

      ws.current?.close(1000);
      ws.current = null;

      ws.current = new WebSocket(getWsUrl(tenantId));
      let messageId = generateSmallUUID();

      ws.current.onopen = () => {
        setIsKeyAssignConnecting(true);

        // In case the banner was open we close it.
        closeBanner?.();
        ws.current?.send(
          JSON.stringify({
            type: EventType.SUBSCRIPTION,
            messageId,
            tenantName: tenantId,
            propertyId: reservation.propertyId,
            tagReaderId,
            context: {
              magicId,
              magicToken,
              magicApiKey: apiKey
            }
          })
        );
      };

      ws.current.onmessage = (ev: MessageEvent) => {
        try {
          const data: SubscriptionEventData = JSON.parse(ev.data);
          if (data.type === EventType.KEEP_ALIVE) {
            return;
          }
          if (data.type === EventType.ERROR) {
            openBanner({
              type: "error",
              title: `Error with status ${data.status} occurred.`
            });
            return;
          }
          if (data.magicId !== magicId) {
            return;
          }
          if (data.type === EventType.SUBSCRIPTION) {
            if (timeout > 0) {
              setTimeout(() => setIsKeyAssignWaiting(true), timeout);
            } else {
              setIsKeyAssignWaiting(true);
            }

            setTimeout(() => {
              if (!isKeyAssigned && ws.current?.readyState === WebSocket.OPEN) {
                setErrorAssigning();

                openBanner({
                  type: "error",
                  title: timeoutError
                });
                ws.current?.close(1000);
              }
            }, 30 * 1000); // force closing connection after 30sec
          }
          if (data.type === EventType.SALTO_TAG) {
            setTagNumber(data.tagNumber);
            ws.current?.close(1000);
            onAssignedKey?.();
            assignCardCallback(reservation, data.tagNumber);
          }
        } catch (e) {
          setErrorAssigning();
          console.error("Something went wrong with handle ws for salto key assign", e);
        }
      };

      ws.current.onclose = () => {
        initializedMagicObjects.current = {};
        setIsKeyAssignWaiting(false);
      };
    }
  }, [
    magicId,
    magicToken,
    initializedMagicObjects,
    isPageVisible,
    apiKey,
    tagReaderId,
    tenantId,
    isKeyAssigned,
    reservation,
    timeoutError,
    assignCardCallback,
    closeBanner,
    openBanner,
    setErrorAssigning,
    setIsKeyAssignConnecting,
    setIsKeyAssignWaiting,
    onAssignedKey,
    timeout
  ]);

  useEffect(() => {
    return () => resetKeyAssignState();
  }, [resetKeyAssignState]);

  return {
    abortKeyAssigment,
    initializeKey,
    isConnecting,
    isWaiting,
    isKeyAssigned,
    tagNumber,
    isError,
    isAborted,
    resetKeyAssignState,
    assignCardCallback,
    onAssignKeySuccess,
    onAssignKeyError
  };
};
